import { forwardRef, useState, useRef, useEffect, useId } from "react";
import type { ForwardedRef, ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import type { FormControlCommonProps } from "../FormControl";
import classNames from "classnames";
import { isFunction, isObject } from "lodash";
import { COLOR_BORDER, TEXT_ALIGN } from "@/comn/features/foundation";

export type InputTextProps = {
    imemode?: "number" | "number+upper" | "number+lower";
    mask?: string | any[] | ((...arg: any) => any);
    exact?: boolean;
    letterCase?: "upper" | "lower";
    trim?: boolean;
    options?: any[];
    openOptions?: (...arg: any) => any;
    onSelectOption?: (...arg: any) => any;
    disableOptionsFilter?: boolean;
    onPaste?: (...arg: any) => any;
};

type Props = InputTextProps & FormControlCommonProps;

export const InputText = forwardRef<HTMLInputElement, Props>((props: Props, ref: ForwardedRef<HTMLInputElement>) => {
    const {
        /* common */
        edit = true,
        mode,
        name,
        value,
        readOnly,
        disabled,
        maxLength,
        placeholder,
        defaultValue,
        onBlur,
        onFocus,
        onChange,
        onEnter,
        onClick,
        onPaste,
        as,
        to,
        align,

        truncate,

        /* style */
        color,
        editColor,
        borderColor,
        bold,
        editBold,
        fontSize,

        /* text */
        imemode,
        mask,
        exact,
        letterCase,
        trim,

        /* ac */
        options,
        openOptions,
        onSelectOption,
        disableOptionsFilter,
    } = props;

    const _props = Object.fromEntries(
        Object.entries({
            name,
            readOnly,
            disabled,
            maxLength,
            placeholder,
            defaultValue,
            onBlur,
            onFocus,
        }).filter(([, value]) => value !== undefined),
    );

    const _context = useRef<any>({ container: null });
    const o = { mask, exact, letterCase, imemode };

    const _key = useRef<boolean>(false);
    const _capture = useRef<Array<any>>([]);
    const [autocompleteOpen, setAutoCompleteOpen] = useState(false);
    const [cursorPosition, setCursorPosition] = useState<any>(0);
    const _ref = useRef<any>(null);

    const getAutocompleteOptions = (o: any, v: any) => {
        const filtered = o?.filter((option: any) => typeof option === "string") || [];
        return filtered.length
            ? disableOptionsFilter
                ? filtered
                : filtered.filter((option: any) => option.toUpperCase().includes((v || "").toUpperCase()))
            : [];
    };

    const toggle = (v: any) => {
        const autocompleteOptions = getAutocompleteOptions(options, v);
        const canOpenAutocomplete =
            (openOptions ? Boolean(openOptions(v)) : true) && Boolean(autocompleteOptions.length);
        setAutoCompleteOpen(canOpenAutocomplete);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const fomrattedValue = formatText(e.target.value, o);
        const nextValue = trim ? fomrattedValue.replaceAll(" ", "") : fomrattedValue;

        toggle(nextValue);
        if (onChange) onChange(nextValue);

        setCapture({ e: "change", start: e.target.selectionStart, end: e.target.selectionEnd });

        const selectionStart = e.target.selectionStart;
        setCursorPosition(selectionStart);
    };

    const handleClick = (event: any) => {
        toggle(value);

        if (onClick) {
            onClick(value);
        }
    };

    const setCapture = (evt: any) => {
        if (_capture.current.length >= 3) {
            _capture.current.shift();
        }
        _capture.current[_capture.current.length] = evt;
    };

    const handleDown = (e: any) => {
        if ((e.code === "Backspace" || e.code === "Delete") && o.mask) {
            _key.current = true;
        }

        if (e.keyCode === 13) {
            /* on enter */
            if (onEnter) {
                onEnter();
            }
        }

        setCapture({ e: "down", start: e.target.selectionStart, end: e.target.selectionEnd });
    };

    /**
     *
     */
    const handleSelectOption = onSelectOption
        ? (optionValue: any) => {
              if (onChange) {
                  if (onSelectOption) {
                      onChange(onSelectOption(value, optionValue));
                  }
              }
          }
        : null;

    const handleSelect = (e: any) => {
        if (_capture.current?.[1]?.e === "select") {
        } else if (_capture.current?.[1]?.e === "change" && _key.current === true) {
            e.target.setSelectionRange(_capture.current[1].start, _capture.current[1].end);
        }
        _key.current = false;
        setCapture({ e: "select", start: e.target.selectionStart, end: e.target.selectionEnd });
    };

    const handleSelectCapture = (e: any) => {
        setCapture({ e: "selectCapture", start: e.target.selectionStart, end: e.target.selectionEnd });
    };

    const EDIT_STATUS = mode === "edit" ? true : mode === "view" ? false : edit;

    useEffect(() => {
        if (!_ref.current || o.mask) return;
        _ref.current.setSelectionRange(cursorPosition, cursorPosition);
    }, [value]);

    const fomrattedValue = formatText(value, o);
    const _value = trim ? fomrattedValue.replaceAll(" ", "") : fomrattedValue;

    const autocompleteOptions = getAutocompleteOptions(options, value);

    return (
        <div
            ref={(element) => {
                if (!element) return;
                _context.current.container = element;
            }}
            className={classNames("w-full", truncate && "grid")}
        >
            {!EDIT_STATUS &&
                (as === "link" ? (
                    to ? (
                        <Link
                            to={to}
                            title={_value}
                            onClick={handleClick}
                            className={classNames("control-text text-blue underline", truncate ? "truncate" : "w-fit")}
                        >
                            {_value}
                        </Link>
                    ) : (
                        <div
                            title={_value}
                            onClick={handleClick}
                            className={classNames(
                                "control-text text-blue underline cursor-pointer break-all",
                                truncate ? "truncate" : "w-fit",
                            )}
                        >
                            {_value}
                        </div>
                    )
                ) : (
                    <div
                        title={_value}
                        className={classNames(
                            "control-text break-all",
                            truncate ? "truncate" : "w-fit",
                            comnUtils.getViewStyle(color, bold, fontSize),
                        )}
                        onClick={handleClick}
                    >
                        {_value}
                    </div>
                ))}

            <div hidden={!EDIT_STATUS}>
                <input
                    {..._props}
                    ref={(element) => {
                        if (!element) return;
                        if (isFunction(ref)) {
                            ref(element);
                        } else if (isObject(ref) && isObject(ref.current)) {
                            ref.current = element;
                        }
                        _ref.current = element;
                    }}
                    value={_value}
                    title={_value}
                    onPaste={onPaste}
                    onClick={handleClick}
                    onChange={handleChange}
                    onKeyDown={handleDown}
                    onSelectCapture={handleSelectCapture}
                    onSelect={handleSelect}
                    type="text"
                    autoComplete="off"
                    className={classNames(
                        "input",
                        comnUtils.getEditStyle(editColor, editBold),
                        (o?.letterCase === "upper" || o?.imemode === "number+upper") && "uppercase",
                        (o?.letterCase === "lower" || o?.imemode === "number+lower") && "lowercase",
                        align && TEXT_ALIGN[align],
                        borderColor ? COLOR_BORDER[borderColor] : "",
                    )}
                />
                {autocompleteOpen && (
                    <Auto
                        inputRef={_ref}
                        inputTextContext={_context}
                        autoComplete={autocompleteOptions}
                        setAutoCompleteOpen={setAutoCompleteOpen}
                        onChange={onChange}
                        handleSelectOption={handleSelectOption}
                    />
                )}
            </div>
        </div>
    );
});

const Auto = (props?: any) => {
    const { inputTextContext, inputRef, autoComplete, onChange, setAutoCompleteOpen, handleSelectOption } = props;
    const containerRect = inputTextContext.current.container.getBoundingClientRect();

    const id = useId();
    const _context = useRef<any>({ autoComplete: [], selected: null });
    const [_selected, _setSelected] = useState<any>(null);

    const handleClose = (event?: any) => {
        if (event?.target === inputRef?.current) {
            return;
        }

        setAutoCompleteOpen(false);
    };

    useEffect(() => {
        _context.current.autoComplete = autoComplete;
    }, [autoComplete]);

    useEffect(() => {
        const handleKeydown = (event: any) => {
            switch (event.keyCode) {
                case 27: // esc
                case 9: // tab
                    handleClose();
                    break;
                case 38: // up
                    _setSelected((prev: any) => {
                        const val = prev === null ? 0 : prev - 1;
                        const next = val < 0 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 40: // down
                    _setSelected((prev: any) => {
                        const val = prev === null ? 0 : prev + 1;
                        const next = val > _context.current.autoComplete.length - 1 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 13: // enter
                    if (typeof _context.current.selected !== "number") return;
                    const value = _context.current.autoComplete[_context.current.selected];
                    if (onChange) {
                        if (handleSelectOption) {
                            handleSelectOption(value);
                        } else {
                            onChange(value);
                        }
                    }
                    setAutoCompleteOpen(false);
                    break;
            }
        };

        window.addEventListener("keydown", handleKeydown);
        window.addEventListener("wheel", handleClose);
        window.addEventListener("scroll", handleClose);
        window.addEventListener("resize", handleClose);
        window.addEventListener("click", handleClose);

        return () => {
            window.removeEventListener("keydown", handleKeydown);
            window.removeEventListener("wheel", handleClose);
            window.removeEventListener("scroll", handleClose);
            window.removeEventListener("resize", handleClose);
            window.removeEventListener("click", handleClose);
        };
    }, []);

    return createPortal(
        <ul
            className="fixed rounded bg-dialog min-w-[12rem] border divide-y shadow z-[9999]"
            onClick={(event) => event.stopPropagation()}
            style={{
                top: containerRect.top + containerRect.height + 4,
                left: containerRect.left,
                width: containerRect.width,
            }}
        >
            {autoComplete.map((item: any, i: any) => {
                return (
                    <li
                        key={id + "." + i}
                        aria-selected={_selected === i}
                        className="flex p-1.5 items-center min-h-[2rem] cursor-pointer hover:bg-lightgray aria-selected:bg-lightgray"
                        onClick={() => {
                            if (onChange) {
                                if (handleSelectOption) {
                                    handleSelectOption(item);
                                } else {
                                    onChange(item);
                                }
                            }
                            setAutoCompleteOpen(false);
                        }}
                    >
                        {item}
                    </li>
                );
            })}
        </ul>,
        document.body,
    );
};

export const formatText = (v: any, o?: any) => {
    if (!v) return "";
    let f = String(v);
    if (o?.letterCase === "upper" || o?.imemode === "number+upper") {
        f = f.toUpperCase();
    }
    if (o?.letterCase === "lower" || o?.imemode === "number+lower") {
        f = f.toLowerCase();
    }
    if (o?.imemode === "number") {
        f = f.replace(/[^0-9]/g, "");
    } else if (o?.imemode === "number+upper") {
        f = f.replace(/[^A-Z0-9]/g, "");
    } else if (o?.imemode === "number+lower") {
        f = f.replace(/[^a-z0-9]/g, "");
    }
    if (o?.mask) {
        const getMasked = (value: any, mask: any) => {
            let pos = 0;
            let temp = "";
            var reg = /[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@\#$%&\\\=\(\'\"\s]/gi;
            let t = value.replace(reg, "").split("");
            let real = "";
            for (var i = 0; i < mask.length; i++) {
                if (typeof mask[i] === "string") {
                    if (t[pos] === undefined) {
                        pos++;
                        continue;
                    }
                    temp += mask[i];
                } else {
                    if (!mask[i].test(t[pos])) {
                        pos++;
                        continue;
                    }
                    if (t[pos] === undefined) break;
                    temp += t[pos];
                    real += t[pos];
                    pos++;
                }
            }
            return temp;
        };

        if (isFunction(o.mask)) {
            f = getMasked(f, o.mask(f));
        } else {
            f = getMasked(f, o.mask);
        }
    }
    return f;
};

export const unformatText = (v: any, o?: any) => {
    if (!v) return undefined;
    let f = String(v);
    if (o?.mask) {
        var reg = /[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@\#$%&\\\=\(\'\"\s]/gi;
        return f.replace(reg, "");
    }
    return f;
};
